// Shape of webshared/controllers/eventPage.cfc's getEventPageData response, after camelizeResponse().
// Booleans coming from a DB tinyint column (hideEntries, hideHeats, disabled, hobbyShop) arrive as
// 0/1, not real JSON booleans — typed loosely here and coerced with truthy() where they're used,
// rather than assumed to be strict `true`/`false`.

export interface EventPageClass {
  id: number
  name: string
  typeName: string
  powerType: string
  disabled: unknown
  priceOverride: number | string
  rules: string
  entered: number
  format: string
}

export interface EventPageSponsor {
  coCode: string
  coName: string
  levelName: string
  // /shared/images/manLogo/{coCode}_logo.gif - a static manufacturer-logo file, same convention
  // api.cfc's own company.logoPath uses. Always populated (coCode is never blank), but the file
  // itself isn't guaranteed to exist for every company - same unverified convention as api.cfc.
  logoUrl: string
}

export interface EventPageExtra {
  id: number
  name: string
  description: string
  type: string
  price: number | string
}

export interface EventPageBroadcast {
  id: number
  title: string
  message: string
  critical: unknown
  createDT: string
}

export interface EventPageRelatedEvent {
  id: number
  name: string
  trackId: number
  // Always a ready-to-use /shared/common/displayImage.cfm URL, or "" when the event has no flyer -
  // no stock-image fallback here (unlike event.flyerUrl), so callers render the text-only card.
  flyerUrl: string
}

// RaceTime Entertainment - a sitewide paid-partner promo (events flagged
// eventprofile.racetimeevent=1), not an attribute of the event this page is showing. Same
// flyerUrl convention as EventPageRelatedEvent; venueName/city/state are already resolved
// server-side (altVenue/altCity/altState vs trackName/city/state), unlike event's own raw pair.
export interface EventPageRaceTimeEvent {
  id: number
  name: string
  flyerUrl: string
  startDT: string
  endDT: string
  venueName: string
  city: string
  state: string
}

// Featured events - a sitewide paid-promotion card (events.cfc::getFeaturedEvents, a paid
// userorders upgrade), not an attribute of the event this page is showing. Same shape as
// EventPageRaceTimeEvent minus flyerUrl - this card renders without a flyer, by design.
export interface EventPageFeaturedEvent {
  id: number
  name: string
  startDT: string
  endDT: string
  venueName: string
  city: string
  state: string
}

export interface EventPageEvent {
  id: number
  name: string
  description: string
  flyerUrl: string
  streamingUrl: string
  hideEntries: unknown
  openPracticeStart: string
  openPracticeEnd: string
  closedPracticeStart: string
  qualifyingStart: string
  mainsStart: string
  hideHeats: unknown
  manualSchedule: string
  trackName: string
  address1: string
  address2: string
  city: string
  state: string
  country: string
  zip: string
  altVenue: string
  altAddress1: string
  altAddress2: string
  altCity: string
  altState: string
  altZip: string
  altCountry: string
  phone: string
  email: string
  www: string
  contactFirstName: string
  contactLastName: string
  promoterFirstName: string
  promoterLastName: string
  promoterPhone: string
  promoterWww: string
  hotelInfo: string
  hobbyShop: unknown
  trackAmenities: string[]
  dollarType: string
  signupWindowOpen: unknown
  full: unknown
  entryLimit: number | string
  entryCount: number | string
  racerLimit: number | string
  racerCount: number | string
  signupUrl: string
  classCost1: number | string
  classCost2: number | string
  classCost3: number | string
  classCost4: number | string
  openPaymentDT: string
  closePaymentDT: string
  entryLineDT: string
}

export interface EventPageViewer {
  loggedIn: unknown
  type: string
  trackId: number | string
  firstName: string
  lastName: string
  // racer-only: racerteam.id if this racer manages a team, else 0 (see session.teamManager)
  teamManager: unknown
  // track-only: empty string means not yet upgraded to a paid Stripe merchant account
  stripeMerchantId: string
  // Always a ready-to-use /shared/common/displayImage.cfm URL (stock-image fallback included),
  // resolved server-side since the racer/track conventions for building it genuinely differ.
  profilePicUrl: string
}

// Shape of eventPage.cfc's getSignupState/addClassEntry/addEventExtra responses - the racer-
// specific signup snapshot, fetched separately from getEventPageData because most viewers of this
// page aren't a logged-in racer (see the "Real class signup" plan).
export interface SignupClassState {
  id: number
  enteredVehicleId: number | string
  amountDue: number | string
  paid: unknown
  full: unknown
  blocked: unknown
}

export interface SignupVehicle {
  id: number
  name: string
  hasTransponder: unknown
}

export interface SignupExtraOption {
  id: number
  label: string
  price: number | string
}

export interface SignupExtraState {
  id: number
  type: string
  added: unknown
  amount: number | string
  paid: unknown
  optionLabel: string
  days: number | string
  options?: SignupExtraOption[]
  maxDays?: number | string
  remaining?: number
}

export interface SignupState {
  success: unknown
  error?: string
  classes: SignupClassState[]
  vehicles: SignupVehicle[]
  classesPerRacer: number | string
  totalEntries: number
  personalOnly: unknown
  classCost1: number | string
  classCost2: number | string
  classCost3: number | string
  classCost4: number | string
  extras: SignupExtraState[]
  checkoutUrl: string
}

export interface EventPageData {
  success: unknown
  error?: string
  event: EventPageEvent
  classes: EventPageClass[]
  sponsors: EventPageSponsor[]
  extras: EventPageExtra[]
  broadcasts: EventPageBroadcast[]
  relatedEvents: EventPageRelatedEvent[]
  raceTimeEvents: EventPageRaceTimeEvent[]
  featuredEvents: EventPageFeaturedEvent[]
  viewer: EventPageViewer
}

/** DB tinyint / CF struct values (0, "0", 1, "1", true, false, "Yes") coerced to a real boolean. */
export function truthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return value !== '' && value !== '0' && value.toLowerCase() !== 'no' && value.toLowerCase() !== 'false'
  return false
}
